import React, { useState } from "react";

import get from "lodash/get";
import { FieldPath, FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useDebounce } from "react-use";
import { InputBaseComponentProps, TextField } from "@mui/material";
import Autocomplete, { AutocompleteProps } from "@mui/material/Autocomplete";

import config from "@/config";
import { mapsApi, referenceApi } from "@/shared/apis";
import { StaticTablePath } from "@/shared/apis/staticTablePath";
import { AddressMaxLength } from "@/validations/address";

import ControllerInput, { ControllerInputProps } from "../ControllerInput/ControllerInput";
import LoadingInput from "../LoadingInput/LoadingInput";

export type LocalityInputProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<ControllerInputProps<TFieldValues, TName>, "render"> &
    Omit<AutocompleteProps<string, boolean, boolean, boolean>, "renderInput" | "options"> & {
        country: FieldPath<TFieldValues>;
        postalCode: FieldPath<TFieldValues>;
    } & Partial<Pick<InputBaseComponentProps, "maxLength">>;

const LocalityInput = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    name,
    control,
    country,
    postalCode,
    LabelProps,
    FormControlProps,
    hideErrorMessage,
    label,
    fullWidth,
    required = false,
    maxLength,
    ...rest
}: LocalityInputProps<TFieldValues, TName>): React.ReactElement => {
    const [countryValue, setCountryValue] = useState("");
    const [postalCodeValue, setPostalCodeValue] = useState("");
    const [searchPostalCode, setSearchPostalCode] = useState("");
    const { t } = useTranslation();
    const { data: countries = [], isLoading } = referenceApi.useStaticQueryWithStatus(StaticTablePath.Country);

    const updateDataSet = () => {
        setCountryValue(get(control._formValues, country));
        setPostalCodeValue(get(control._formValues, postalCode));
    };

    const countryName = countries.find((c) => c.id.toString() === countryValue)?.name || countryValue;

    useDebounce(
        () => {
            if (postalCodeValue && postalCodeValue.length > 2) {
                setSearchPostalCode(postalCodeValue);
            } else {
                setSearchPostalCode("");
            }
        },
        400,
        [postalCodeValue],
    );

    const { data = [] } = useQuery(
        ["localities", { countryName, searchPostalCode }],
        () => mapsApi.getLocalitiesSuggestions(postalCodeValue, countryName),
        { keepPreviousData: true, suspense: false, enabled: config.FEATURES.useGoogleMapsApi },
    );

    if (isLoading) return <LoadingInput />;

    return (
        <ControllerInput
            label={label || label === "" ? label : t("Common.City")}
            LabelProps={LabelProps}
            FormControlProps={FormControlProps}
            required={required}
            fullWidth={fullWidth}
            hideErrorMessage={hideErrorMessage}
            control={control}
            name={name}
            render={({ field: { onChange, onBlur, value, ref }, error }) => (
                <Autocomplete<string, boolean, boolean, boolean>
                    classes={{
                        input: "autocomplete-input",
                    }}
                    value={value}
                    onFocus={(_event) => {
                        if (config.FEATURES.useGoogleMapsApi) updateDataSet();
                    }}
                    onBlur={onBlur}
                    onChange={(_event, newValue) => {
                        onChange(newValue ?? "");
                    }}
                    handleHomeEndKeys
                    renderOption={(props, option) => <li {...props}>{option}</li>}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            inputRef={ref}
                            error={!!error}
                            InputProps={{
                                ...params.InputProps,
                                inputProps: {
                                    name,
                                    ...params.inputProps,
                                    maxLength: AddressMaxLength.City,
                                    id: name,
                                    autoComplete: "no",
                                },
                            }}
                        />
                    )}
                    autoSelect
                    freeSolo
                    options={data}
                    {...rest}
                />
            )}
        />
    );
};

export default LocalityInput;
